import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../lib/cn.js'
import { useConnectionState } from '../lib/realtime.js'
import { useSession } from '../lib/session.js'
import { errorMessage, setBearerToken, useTRPC } from '../lib/trpc.js'
import { FieldShell } from './field/FieldShell.js'
import { useAppVersion } from '../lib/app-version.js'
import { Icon } from './Icon.js'
import { JobOverlay } from './JobOverlay.js'
import { useIsField } from '../lib/media.js'
import { NAV, activeNavItem, useUnreadCount, type NavItem } from './nav.js'
import { Avatar, Badge } from './ui.js'
import { Wordmark } from './Wordmark.js'

/**
 * The application frame.
 *
 * One component serves both surfaces rather than two codebases: a 72px icon
 * rail on a wide screen, a bottom bar on a narrow one. The bottom bar is
 * where the thumb is, and it carries only the four things a technician needs in
 * the field — everything else lives behind "More", because a nine-item bottom
 * bar is unusable at arm's length in a van.
 *
 * The rail is light and narrow on purpose. The board lives on an ink-100 page
 * of white cards, and a dark strip beside it was a second colour system for
 * nine icons that never change. Ink-100 matches the page; the active item is
 * Buster Navy, the job card's "on", not green (ADR 030). The 9px label is a reminder rather than a signpost, so
 * every item keeps a real accessible name and the mobile bar keeps its full
 * text.
 *
 * Settings is a rail item as well as the avatar at the foot of it, because an
 * account-wide screen that can only be reached by pressing your own face is a
 * screen people report as missing. The avatar stays: it is the only element
 * always on screen, so it carries the connection state.
 *
 * People is a Settings section, not a tenth rail item. Decision 65. A person
 * page still lights Settings, because that is where you came from.
 *
 * **Tasks is the tenth, and only for the office.** Decision 38 said nine fit
 * and to revisit when a tenth screen wanted a place; this one does, and the
 * `TASK_MANAGE` gate means a technician's rail stays at eight while the
 * office's goes to ten — which still does not scroll at any desktop height the
 * board is usable at. It is not `primary`, so the four-item bottom bar is
 * untouched and Tasks lives behind "More" on a phone.
 */

function titleFor(pathname: string): string {
  if (pathname.startsWith('/people/')) return 'People'
  return activeNavItem(NAV, pathname)?.label ?? 'Today'
}

export function AppShell() {
  const { can } = useSession()
  const field = useIsField()
  const visible = NAV.filter((item) => !item.permission || can(item.permission))

  // A phone, and any touch-first screen at any width, is the field app: the
  // technician's tabs and a job that slides up. Decision 93.
  if (field) return <FieldShell />

  return (
    <div className="flex h-full bg-ink-100">
      <DesktopRail items={visible} />

      <div className="flex min-w-0 flex-1 flex-col">
        <MobileHeader />
        <main className="min-h-0 flex-1 overflow-y-auto pb-[calc(4.5rem+var(--safe-bottom))] lg:pb-0">
          <Outlet />
        </main>
        <MobileNav items={visible} />
      </div>

      {/* Every screen can open a job, so the popup belongs to the shell rather
          than to any of them. Decision 61. */}
      <JobOverlay />
    </div>
  )
}

function DesktopRail({ items }: { items: NavItem[] }) {
  const { user } = useSession()
  const connection = useConnectionState()
  const location = useLocation()
  const current = activeNavItem(items, location.pathname)

  const offline =
    connection === 'connecting' ? 'Reconnecting…' : connection === 'open' ? null : 'Offline — changes may be stale'

  return (
    <aside className="hidden w-[var(--sidebar-width)] shrink-0 flex-col items-center gap-1 bg-ink-100 py-2.5 lg:flex">
      <Wordmark className="mb-2 w-9" />

      <nav className="flex flex-1 flex-col items-center gap-1">
        {items.map((item) => {
          const active = item === current
          return (
            <Link
              key={item.to}
              to={item.to}
              title={item.label}
              aria-current={active ? 'page' : undefined}
              className={cn(
                'relative flex w-14 flex-col items-center gap-[3px] rounded-lg py-1.5 transition-colors',
                active
                  ? 'bg-brand-800 font-semibold text-white'
                  : 'text-ink-500 hover:bg-white/70 hover:text-brand-800',
              )}
            >
              <Icon name={item.icon} />
              <span className="text-[9px] font-semibold">{item.label}</span>
              {item.to === '/inbox' ? <UnreadDot className="top-1 right-2.5" /> : null}
            </Link>
          )
        })}
      </nav>

      <AccountMenu offline={offline} />
    </aside>
  )
}

/**
 * The avatar opens a short menu rather than navigating, so signing out is two
 * clicks from any screen instead of a trip to Settings → Account. My profile
 * is only offered to somebody who can open person pages — `users.get` is
 * `USER_MANAGE`, and a link to a page that refuses you is worse than none.
 *
 * The wide sidebar had room to write the connection state out. Here it is an
 * amber ring on the avatar, which is the only element that is always on
 * screen, with the sentence in the tooltip and at the top of the menu.
 */
function AccountMenu({ offline }: { offline: string | null }) {
  const { user, can } = useSession()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onPointerDown = (native: PointerEvent) => {
      if (native.target instanceof Node && root.current?.contains(native.target)) return
      setOpen(false)
    }
    const onKeyDown = (native: KeyboardEvent) => {
      if (native.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const logout = useMutation(
    trpc.auth.logout.mutationOptions({
      onSuccess: () => {
        setBearerToken(null)
        queryClient.clear()
        navigate('/login', { replace: true })
      },
    }),
  )

  const name = user?.name ?? 'Account'
  const version = useAppVersion()
  const versionLine = version.current ?? version.behind
  const itemClass =
    'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-ink-700 hover:bg-ink-50 hover:text-ink-900'

  return (
    <div ref={root} className="relative mt-auto">
      <button
        type="button"
        aria-label="Account"
        aria-haspopup="menu"
        aria-expanded={open}
        title={offline ? `${name} · ${offline}` : name}
        onClick={() => setOpen((was) => !was)}
        className="block rounded-full"
      >
        <Avatar
          name={user?.name ?? '?'}
          colour={user?.colour}
          className={cn('size-7', offline && 'ring-2 ring-amber-500')}
        />
      </button>

      {open ? (
        <div
          role="menu"
          aria-label="Account"
          className="absolute bottom-0 left-full z-50 ml-3 w-60 overflow-hidden rounded-xl bg-white py-1 shadow-[0_16px_40px_rgb(15_23_42_/_0.22),0_4px_12px_rgb(15_23_42_/_0.1)] ring-1 ring-ink-200"
        >
          <div className="border-b border-ink-100 px-3 pt-2 pb-2.5">
            <p className="truncate text-sm font-semibold text-ink-900">{name}</p>
            {user?.email ? <p className="truncate text-xs text-ink-500">{user.email}</p> : null}
            {offline ? <p className="mt-1 text-xs text-amber-700">{offline}</p> : null}
            {versionLine ? <p className="mt-1 text-xs text-ink-400">{versionLine}</p> : null}
          </div>

          <Link
            role="menuitem"
            to="/settings?tab=account"
            onClick={() => setOpen(false)}
            className={itemClass}
          >
            <Icon name="settings" className="size-4" />
            Account settings
          </Link>
          {user && can('USER_MANAGE') ? (
            <Link
              role="menuitem"
              to={`/people/${user.id}`}
              onClick={() => setOpen(false)}
              className={itemClass}
            >
              <Icon name="clients" className="size-4" />
              My profile
            </Link>
          ) : null}

          <div className="my-1 border-t border-ink-100" />

          <button
            type="button"
            role="menuitem"
            disabled={logout.isPending}
            onClick={() => logout.mutate()}
            className={cn(itemClass, 'disabled:opacity-60')}
          >
            <Icon name="signOut" className="size-4" />
            {logout.isPending ? 'Signing out…' : 'Sign out'}
          </button>
          {logout.error ? (
            <p className="px-3 pb-2 text-xs text-red-600">{errorMessage(logout.error)}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

function MobileHeader() {
  const location = useLocation()
  const connection = useConnectionState()

  const title = titleFor(location.pathname)

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between gap-3 border-b border-ink-200 bg-white/90 px-4 pb-3 pt-[calc(0.75rem+var(--safe-top))] backdrop-blur lg:hidden">
      <h1 className="text-base font-semibold text-ink-900">{title}</h1>
      {connection !== 'open' ? (
        <Badge tone="amber">{connection === 'connecting' ? 'Reconnecting' : 'Offline'}</Badge>
      ) : null}
    </header>
  )
}

function MobileNav({ items }: { items: NavItem[] }) {
  const [moreOpen, setMoreOpen] = useState(false)

  const primary = items.filter((item) => item.primary)
  const sheet = items.filter((item) => !item.primary)

  return (
    <>
      {moreOpen ? (
        <div
          className="fixed inset-0 z-30 bg-ink-900/40 lg:hidden"
          onClick={() => setMoreOpen(false)}
        >
          <div
            className="absolute inset-x-0 bottom-0 rounded-t-2xl bg-white p-3 pb-[calc(0.75rem+var(--safe-bottom))]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-ink-300" />
            <div className="grid grid-cols-3 gap-2">
              {sheet.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMoreOpen(false)}
                  className="flex min-h-20 flex-col items-center justify-center gap-1.5 rounded-xl bg-ink-50 text-xs font-medium text-ink-700"
                >
                  <Icon name={item.icon} />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
        </div>
      ) : null}

      <nav className="fixed inset-x-0 bottom-0 z-20 flex border-t border-ink-200 bg-white/95 pb-[var(--safe-bottom)] backdrop-blur lg:hidden">
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/'}
            className={({ isActive }) =>
              cn(
                'relative flex min-h-16 flex-1 flex-col items-center justify-center gap-1 text-[10px] font-semibold',
                isActive ? 'text-brand-800' : 'text-ink-500',
              )
            }
          >
            <Icon name={item.icon} />
            {item.label}
            {item.to === '/inbox' ? <UnreadDot /> : null}
          </NavLink>
        ))}

        <button
          type="button"
          onClick={() => setMoreOpen(true)}
          className="flex min-h-16 flex-1 flex-col items-center justify-center gap-1 text-[10px] font-semibold text-ink-500"
        >
          <Icon name="more" />
          More
        </button>
      </nav>
    </>
  )
}

/**
 * Unread mail is a dot, not a number, on both surfaces. The rail has no room
 * for a count and the bottom bar has no room to spare; either way what a
 * person does about it is the same.
 */
function UnreadDot({ className }: { className?: string }) {
  const count = useUnreadCount()
  if (!count) return null

  return (
    <span
      aria-label={`${count} unread`}
      className={cn('absolute top-2 right-1/2 ml-4 size-2 rounded-full bg-brand-600', className)}
    />
  )
}
